import * as React from 'react';

import { Input, InputProps } from '@lightspeed/flame/Input';

import { formatNumberInput, maxMoney } from 'pages/utils/input';
import { toBoolean } from 'root/apps/pages/utils/sanitize';
import {
  OverviewDataContext,
  ShipmentItemPayload,
} from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/contexts/OverviewData';

import { setModalQuantity } from './ModalQuantity';

import type { ShipmentItemResponse } from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface Props {
  name: keyof ShipmentItemPayload;
  value: string | number | undefined;
  orderId: string | number;
  shipmentId: string | number;
  shipmentItemId: string | number;
  isDisabled: boolean;
  callback?: (data: ShipmentItemResponse) => void;
}

const QuantityInput: React.FC<Props & InputProps> = ({
  name,
  value: rawOriginalValue,
  orderId,
  shipmentId,
  shipmentItemId,
  isDisabled,
  callback,
  ...rest
}) => {
  const [originalValue, setOriginalValue] = React.useState(parseInt(`${rawOriginalValue}`, 10));
  const [value, setValue] = React.useState(parseInt(`${rawOriginalValue}`, 10));
  const { saveItemData, itemIdSaving } = React.useContext(OverviewDataContext);
  const hasEditRight =
    toBoolean(window.merchantos.session.sessData!.Rights!.product_edit) &&
    toBoolean(window.merchantos.session.sessData!.Rights!.purchase_orders_receive);

  const changeQuantity = (newValue: number) => {
    if (originalValue === newValue) return;

    setModalQuantity({
      cancelCallback: () => setValue(originalValue),
      confirmCallback: async () => {
        const savedData = await saveItemData({
          orderId,
          shipmentId,
          shipmentItemId,
          payload: { [name]: newValue },
        });

        if (savedData) {
          callback?.(savedData);
          setOriginalValue(newValue);
        } else {
          setValue(originalValue);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
    });
  };

  return (
    <Input
      type="number"
      max={parseInt(`${maxMoney}`, 10)}
      min={0}
      placeholder="0"
      value={value}
      disabled={!hasEditRight || isDisabled || itemIdSaving === shipmentItemId}
      name={name}
      id={`${name}-input`}
      aria-label={`${name}-input`}
      onFocus={(event) => {
        window.selectField(event.target);
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          // @ts-ignore
          event.target.blur();
        }
      }}
      onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
        setValue(parseInt(value, 10))
      }
      onBlur={({ target: { value } }) => {
        const newValue = parseInt(formatNumberInput(value || 0), 10);

        setValue(newValue);
        changeQuantity(newValue);
      }}
      {...rest}
    />
  );
};

export default QuantityInput;
